import { supabase } from './supabase'

/**
 * Normaliza um domínio removendo barras, www e espaços
 */
function normalizeDomain(domain: string): string {
  return domain
    .trim()
    .toLowerCase()
    .replace(/\/$/, '') // Remove barra final
    .replace(/^www\./, '') // Remove www.
}

/**
 * Detecta o ID da igreja baseado no domínio atual
 * @param domain - Domínio da requisição (ex: 'localhost:3000', 'minhaigreja.com')
 * @returns Promise com o ID da igreja ou null se não encontrada
 */
export async function getIgrejaIdByDomain(domain?: string): Promise<string | null> {
  try {
    console.log('🔵 [getIgrejaIdByDomain] Iniciando detecção de igreja...')

    // Se não foi fornecido um domínio, tenta pegar do window.location
    const currentHost = domain || (typeof window !== 'undefined' ? window.location.host : null)

    console.log('🔵 [getIgrejaIdByDomain] currentHost:', currentHost)
    console.log('🔵 [getIgrejaIdByDomain] window.location:', typeof window !== 'undefined' ? {
      host: window.location.host,
      hostname: window.location.hostname,
      href: window.location.href,
      protocol: window.location.protocol,
      origin: window.location.origin
    } : 'window não disponível')

    if (!currentHost || !supabase) {
      console.error('❌ [getIgrejaIdByDomain] currentHost ou supabase não disponível:', { currentHost, supabase: !!supabase })
      return null
    }

    // Detectar protocolo (http ou https)
    const protocol = typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'https' : 'http'
    const currentDomain = normalizeDomain(`${protocol}://${currentHost}`)

    console.log('🔍 [getIgrejaIdByDomain] Buscando igreja com:', {
      currentHost,
      protocol,
      currentDomain,
      windowProtocol: typeof window !== 'undefined' ? window.location.protocol : 'N/A'
    })

    // Primeiro, buscar todas as igrejas ativas para comparar
    const { data: allChurches, error: listError } = await supabase
      .from('igrejas')
      .select('id, nome, dominio, ativa')
      .eq('ativa', true)

    if (listError) {
      console.error('❌ [getIgrejaIdByDomain] Erro ao listar igrejas:', listError)
      return null
    }

    console.log('📋 [getIgrejaIdByDomain] Igrejas ativas no banco:', allChurches)

    // Tentar encontrar correspondência exata primeiro (normalizada)
    let matchedChurch = allChurches?.find(church => {
      const normalizedDbDomain = normalizeDomain(church.dominio || '')
      return normalizedDbDomain === currentDomain
    })

    // Se não encontrou com https, tentar com http
    if (!matchedChurch && protocol === 'https') {
      const httpDomain = normalizeDomain(`http://${currentHost}`)
      console.log('⚠️ [getIgrejaIdByDomain] Tentando com http:', httpDomain)

      matchedChurch = allChurches?.find(church => {
        const normalizedDbDomain = normalizeDomain(church.dominio || '')
        return normalizedDbDomain === httpDomain
      })
    }

    // Se ainda não encontrou, tentar buscar apenas pelo host (sem protocolo)
    if (!matchedChurch) {
      console.log('⚠️ [getIgrejaIdByDomain] Tentando buscar pelo host apenas:', currentHost)

      matchedChurch = allChurches?.find(church => {
        const dbDomain = church.dominio || ''
        return dbDomain.toLowerCase().includes(currentHost.toLowerCase())
      })
    }

    if (!matchedChurch) {
      console.error('❌ [getIgrejaIdByDomain] Igreja não encontrada após todas as tentativas')
      console.error('❌ [getIgrejaIdByDomain] Domínio buscado:', currentDomain)
      console.error('❌ [getIgrejaIdByDomain] Host buscado:', currentHost)
      console.error('❌ [getIgrejaIdByDomain] Protocol usado:', protocol)
      console.error('❌ [getIgrejaIdByDomain] Domínios disponíveis:', allChurches?.map(c => c.dominio))

      return null
    }

    console.log('✅ [getIgrejaIdByDomain] Igreja encontrada:', {
      id: matchedChurch.id,
      nome: matchedChurch.nome,
      dominio: matchedChurch.dominio,
      currentDomain,
      currentHost
    })
    return matchedChurch.id
  } catch (error) {
    console.error('❌ [getIgrejaIdByDomain] Erro inesperado:', error)
    return null
  }
}

/**
 * Obtém informações completas da igreja pelo domínio
 * @param domain - Domínio da requisição
 * @returns Promise com os dados da igreja ou null
 */
export async function getIgrejaByDomain(domain?: string) {
  try {
    const currentHost = domain || (typeof window !== 'undefined' ? window.location.host : null)

    console.log('🏛️ [getIgrejaByDomain] Buscando igreja para host:', currentHost)

    if (!currentHost || !supabase) {
      console.log('❌ [getIgrejaByDomain] Host ou supabase não disponível')
      return null
    }

    // Detectar protocolo (http ou https)
    const protocol = typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'https' : 'http'
    const currentDomain = normalizeDomain(`${protocol}://${currentHost}`)

    console.log('🔍 [getIgrejaByDomain] Domínio normalizado:', currentDomain)

    // Buscar todas as igrejas ativas
    const { data: allChurches, error: listError } = await supabase
      .from('igrejas')
      .select('*')
      .eq('ativa', true)

    if (listError) {
      console.error('Erro ao listar igrejas:', listError)
      return null
    }

    console.log('📋 [getIgrejaByDomain] Igrejas encontradas:', allChurches?.length)
    console.log('📋 [getIgrejaByDomain] Domínios das igrejas:', allChurches?.map(c => ({ nome: c.nome, dominio: c.dominio })))

    // Tentar encontrar correspondência exata primeiro (normalizada)
    let matchedChurch = allChurches?.find(church => {
      const normalizedDbDomain = normalizeDomain(church.dominio || '')
      console.log('🔍 Comparando:', { normalizedDbDomain, currentDomain, match: normalizedDbDomain === currentDomain })
      return normalizedDbDomain === currentDomain
    })

    // Se não encontrou com https, tentar com http
    if (!matchedChurch && protocol === 'https') {
      const httpDomain = normalizeDomain(`http://${currentHost}`)
      console.log('🔄 Tentando com HTTP:', httpDomain)
      matchedChurch = allChurches?.find(church => {
        const normalizedDbDomain = normalizeDomain(church.dominio || '')
        return normalizedDbDomain === httpDomain
      })
    }

    // Se ainda não encontrou, tentar buscar apenas pelo host (sem protocolo)
    if (!matchedChurch) {
      console.log('🔄 Tentando busca por host apenas:', currentHost)
      matchedChurch = allChurches?.find(church => {
        const dbDomain = church.dominio || ''
        return dbDomain.toLowerCase().includes(currentHost.toLowerCase())
      })
    }

    console.log('✅ [getIgrejaByDomain] Igreja encontrada:', matchedChurch ? { nome: matchedChurch.nome, logo_light: matchedChurch.logo_light } : 'Nenhuma')

    return matchedChurch || null
  } catch (error) {
    console.error('Erro inesperado ao buscar igreja:', error)
    return null
  }
}